import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "hooks/useAuth";
import { useAuthStore } from "store/authStore";
import { usePackagesOptional } from "contexts/PackagesContext";
import { companiesService } from "services/companies.service";
import { usersService } from "services/users.service";
import { packagesService } from "services/packages.service";
import { authService } from "services/auth.service";
import { resolveIsIndividualAccount } from "utils/resolveIndividualAccount";
import { isAdminRole, isIndividualRole } from "utils/roles";
import {
  getCachedIndividualAccount,
  setCachedIndividualAccount,
} from "utils/accountTypeCache";
import { queryKeys } from "lib/queryKeys";
import type { User } from "types/user";

function syncAccountTypeToUser(
  companyId: string,
  isIndividual: boolean,
  user: User | null
): void {
  setCachedIndividualAccount(companyId, isIndividual);
  if (!user) return;
  const next = { ...user, isIndividualAccount: isIndividual };
  authService.setUser(next);
  useAuthStore.getState().updateUser(next);
}

export function useIsIndividualAccount() {
  const { user } = useAuth();
  const { packages: cachedPackages } = usePackagesOptional();
  const isCompanyUser = Boolean(
    user && user.roleId && Number(user.roleId) !== 1
  );
  const companyId = user?.company_id;
  const individualId = user?.individual_id;
  const isIndividualByAccount =
    isIndividualRole(user?.roleId) ||
    Boolean(individualId && individualId !== "0");

  const cached =
    companyId != null && companyId !== ""
      ? getCachedIndividualAccount(companyId)
      : undefined;
  const initial =
    user?.isIndividualAccount ?? cached ?? (isIndividualByAccount ? true : undefined);

  const query = useQuery({
    queryKey: queryKeys.company.accountType(
      `${companyId ?? ""}-${individualId ?? ""}-${user?.id ?? ""}`
    ),
    queryFn: async () => {
      if (isIndividualByAccount) return true;
      if (!companyId) return false;

      const company = await companiesService.getById(companyId);

      let packages = cachedPackages;
      if (packages.length === 0) {
        packages = await packagesService.getAll();
      }

      let packageDetailsId: string | number | null | undefined =
        user?.packageDetailsId ?? user?.packageId;

      if (user?.id && isAdminRole(user.roleId)) {
        try {
          const fullUser = await usersService.getById(String(user.id));
          packageDetailsId =
            fullUser.packageDetailsId ?? packageDetailsId;
        } catch {
          /* use company/package fallback */
        }
      }

      return resolveIsIndividualAccount({
        company,
        packages,
        packageDetailsId,
      });
    },
    enabled: isCompanyUser && (isIndividualByAccount || Boolean(companyId)),
    staleTime: 5 * 60_000,
    initialData: initial,
    placeholderData: initial,
  });

  useEffect(() => {
    if (!companyId || query.data === undefined) return;
    syncAccountTypeToUser(companyId, query.data, user);
  }, [companyId, query.data, user?.id]);

  const isIndividualAccount =
    isIndividualByAccount || (query.data ?? initial ?? false);
  const isResolved =
    !isCompanyUser ||
    isIndividualByAccount ||
    !companyId ||
    query.isFetched ||
    initial !== undefined;

  return {
    ...query,
    data: isIndividualAccount,
    isIndividualAccount,
    isCompanyUser,
    isResolved,
  };
}
